/* RESPONSIBLE TEAM: team-reporting */

import { addDays, differenceInCalendarDays } from 'date-fns';

import {
  SECONDS_PER_DAY,
  SECONDS_PER_HOUR,
  SECONDS_PER_MINUTE,
  SECONDS_PER_MONTH,
  SECONDS_PER_YEAR,
} from '@/features/reporting-charts/constants';
import type {
  ChartConfig,
  ChartData,
  ChartDataResponse,
  DateRange,
  DimensionCell,
  FractionUnit,
  MetricUnit,
  RenderContext,
} from '@/features/reporting-charts/types';

/**
 * Formats a numeric value based on its metric unit and locale.
 * Handles percentages, durations (seconds/minutes/hours), ratios, and plain values.
 */
export function formatValue(
  value: number | null,
  unit: MetricUnit,
  renderContext: RenderContext,
): string {
  if (value === null) return '-';

  switch (unit) {
    case 'percent':
      return `${value.toFixed(1).replace(/\.0$/, '')}%`;

    case 'seconds':
      return formatDuration(value, 'seconds');

    case 'minutes':
      return formatDuration(value, 'minutes');

    case 'hours':
      return formatDuration(value, 'hours');

    case 'ratio':
    case 'score':
      return value.toFixed(1);

    case 'value':
    default: {
      const rounded = value % 1 !== 0 ? Number(value.toFixed(1)) : value;
      return new Intl.NumberFormat(renderContext.locale).format(rounded);
    }
  }
}

/**
 * Formats a duration value to a human-readable string.
 * Accepts the value in its native unit and converts internally.
 * Examples: "45s", "5m 30s", "2h 15m"
 */
export function formatDuration(value: number, unit: 'seconds' | 'minutes' | 'hours'): string {
  // Convert to seconds for uniform formatting
  let seconds: number;
  switch (unit) {
    case 'hours':
      seconds = value * SECONDS_PER_HOUR;
      break;
    case 'minutes':
      seconds = value * SECONDS_PER_MINUTE;
      break;
    case 'seconds':
    default:
      seconds = value;
  }

  if (seconds < SECONDS_PER_MINUTE) return `${Math.round(seconds)}s`;
  if (seconds < SECONDS_PER_HOUR) {
    const mins = Math.floor(seconds / SECONDS_PER_MINUTE);
    const secs = Math.round(seconds % SECONDS_PER_MINUTE);
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }
  if (seconds < SECONDS_PER_DAY) {
    const hours = Math.floor(seconds / SECONDS_PER_HOUR);
    const mins = Math.floor((seconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const days = Math.floor(seconds / SECONDS_PER_DAY);
  const hours = Math.floor((seconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
  return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
}

/**
 * Formats a duration as a single rounded unit for compact display (e.g., heatmap cells).
 * Matches Ember's formatRoundedTime behavior (duration-formatter.js 'rounded-default'):
 * rounds to the nearest single unit using the same thresholds.
 * Examples: "45s", "6m", "2h", "7d", "6mo", "2y"
 */
export function formatRoundedDuration(
  value: number,
  unit: 'seconds' | 'minutes' | 'hours',
): string {
  let seconds: number;
  switch (unit) {
    case 'hours':
      seconds = value * SECONDS_PER_HOUR;
      break;
    case 'minutes':
      seconds = value * SECONDS_PER_MINUTE;
      break;
    case 'seconds':
    default:
      seconds = value;
  }

  if (seconds < SECONDS_PER_MINUTE) {
    return `${Math.round(seconds)}s`;
  }
  if (seconds < SECONDS_PER_HOUR) {
    return `${Math.round(seconds / SECONDS_PER_MINUTE)}m`;
  }
  if (seconds < SECONDS_PER_DAY) {
    return `${Math.round(seconds / SECONDS_PER_HOUR)}h`;
  }
  if (seconds < SECONDS_PER_MONTH) {
    return `${Math.round(seconds / SECONDS_PER_DAY)}d`;
  }
  if (seconds < SECONDS_PER_YEAR) {
    return `${Math.round(seconds / SECONDS_PER_MONTH)}mo`;
  }
  return `${Math.round(seconds / SECONDS_PER_YEAR)}y`;
}

/**
 * Formats a metric value using single-unit rounding for compact display contexts
 * (e.g., heatmap cell labels). Duration units use formatRoundedDuration;
 * non-duration units fall through to formatValue.
 */
export function formatRoundedValue(
  value: number | null,
  unit: MetricUnit,
  renderContext: RenderContext,
): string {
  if (value === null) return '-';

  switch (unit) {
    case 'seconds':
      return formatRoundedDuration(value, 'seconds');
    case 'minutes':
      return formatRoundedDuration(value, 'minutes');
    case 'hours':
      return formatRoundedDuration(value, 'hours');
    case 'percent':
      return `${Math.round(value)}%`;
    default:
      return formatValue(value, unit, renderContext);
  }
}

/**
 * Formats a numeric value with compact notation for space-constrained labels
 * (e.g., target lines). Produces "2K", "1.5M" etc. for value units.
 * Non-value units already produce compact representations via formatValue().
 */
export function formatCompactValue(
  value: number | null,
  unit: MetricUnit,
  renderContext: RenderContext,
): string {
  if (value === null) return '-';
  if (unit !== 'value') return formatValue(value, unit, renderContext);

  return new Intl.NumberFormat(renderContext.locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Finds a specific time period from the chart data response.
 * Returns the data for 'current' or 'previous' period, or null if not found.
 */
export function findPeriod(
  data: ChartDataResponse,
  period: 'current' | 'previous',
): ChartData | null {
  return data.find((d) => d.period === period) ?? null;
}

/**
 * Formats a timestamp in milliseconds to a human-readable string.
 * Examples: "Jan 1", "Jan 1, 2024"
 */

export function formatTimeTick(
  ms: number,
  interval: ChartConfig['viewByTimeInterval'],
  renderContext: RenderContext,
): string {
  if (interval === 'hour') {
    // "12AM", "2PM" etc. — matches Ember's %l%p format
    return new Intl.DateTimeFormat(renderContext.locale, {
      timeZone: renderContext.timezone,
      hour: 'numeric',
      hour12: true,
    })
      .format(new Date(ms))
      .replace(/\s/g, '');
  }

  const options: Intl.DateTimeFormatOptions =
    interval === 'month' ? { month: 'short' } : { month: 'short', day: 'numeric' };

  return new Intl.DateTimeFormat(renderContext.locale, {
    timeZone: renderContext.timezone,
    ...options,
  }).format(new Date(ms));
}

/**
 * Formats a timestamp for display in chart legends and labels based on time interval.
 * For 'week' interval: shows date range like "Dec 29 - Jan 4"
 * For 'month' interval: shows month name like "Jan"
 * For other intervals: shows date like "Jan 1"
 */
export function formatTimeDimensionLabel(
  timestamp: number,
  interval: ChartConfig['viewByTimeInterval'],
  renderContext: RenderContext,
  periodEndDate?: Date,
): string {
  const date = new Date(timestamp);
  const formatter = new Intl.DateTimeFormat(renderContext.locale, {
    month: 'short',
    day: 'numeric',
    timeZone: renderContext.timezone,
  });

  if (interval === 'week') {
    // For weekly data, show date range: "Dec 29 - Jan 4"
    // When periodEndDate is provided (last data point), cap the end to the actual period end
    const fullWeekEnd = addDays(date, 6);
    const endDate = periodEndDate && periodEndDate < fullWeekEnd ? periodEndDate : fullWeekEnd;
    return `${formatter.format(date)} - ${formatter.format(endDate)}`;
  }

  if (interval === 'month') {
    // For monthly data, show just the month: "Jan"
    const monthFormatter = new Intl.DateTimeFormat(renderContext.locale, {
      month: 'short',
      timeZone: renderContext.timezone,
    });
    return monthFormatter.format(date);
  }

  if (interval === 'day') {
    // For daily data, show date with weekday: "Jan 1, Wed"
    const weekdayFormatter = new Intl.DateTimeFormat(renderContext.locale, {
      weekday: 'short',
      timeZone: renderContext.timezone,
    });
    return `${formatter.format(date)}, ${weekdayFormatter.format(date)}`;
  }

  // For hour intervals, show date: "Jan 1"
  return formatter.format(date);
}

/**
 * Recomputes a ratio value when the fractionUnit requires unit conversion.
 * For example, when denominator is 'seconds', it converts to hours (÷SECONDS_PER_HOUR)
 * before computing numerator/denominator.
 */
export function applyFractionUnit(
  cell: { value: number; numeratorValue: number; denominatorValue: number },
  fractionUnit: FractionUnit | undefined,
): number {
  if (!fractionUnit || cell.denominatorValue === 0) return cell.value;

  let denominator = cell.denominatorValue;
  switch (fractionUnit.denominator) {
    case 'seconds':
      denominator = denominator / SECONDS_PER_HOUR;
      break;
    default:
      return cell.value;
  }

  const result = cell.numeratorValue / denominator;
  return Number.isFinite(result) ? result : cell.value;
}

/**
 * Formats a period label for legend display.
 * Current period: "01 Jan - 21 Jan" format (matches legacy formatDateRangeWithoutYear)
 * Previous period: "Previous 28 days" or "Previous 1 day" format (English-only, singular/plural)
 */
export function formatPeriodLabel(
  dateRange: DateRange | undefined,
  period: 'current' | 'previous',
  locale: string,
  timezone: string,
): string | undefined {
  if (!dateRange) return undefined;

  const range = period === 'current' ? dateRange.current : dateRange.previous;
  if (!range) return undefined;

  if (period === 'previous') {
    const numberOfDays = differenceInCalendarDays(range.end, range.start) + 1;

    if (numberOfDays === 1) {
      return `Previous ${numberOfDays} day`;
    } else {
      return `Previous ${numberOfDays} days`;
    }
  }

  const formatter = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    timeZone: timezone,
  });

  const start = formatter.format(range.start);
  const end = formatter.format(range.end);

  if (start === end) {
    return start;
  }

  return `${start} - ${end}`;
}

/**
 * Returns the best display label for a dimension cell.
 * Checks resolvedValue for range-type data (e.g., bucketed time ranges like "0-30s"),
 * then falls back to the cell's displayValue.
 */
export function formatDimensionDisplayValue(cell: DimensionCell): string {
  if (cell.resolvedValue?.type === 'range') {
    return formatRangeDimensionLabel(cell.resolvedValue);
  }
  return String(cell.displayValue);
}

/**
 * Formats a range dimension (e.g., bucketed response time) into a human-readable label.
 * Examples: "< 30s", "30s - 2m", "> 1h"
 */
export function formatRangeDimensionLabel(resolved: Record<string, unknown>): string {
  const from = resolved.from as number | undefined;
  const to = resolved.to as number | undefined;
  const unit = (resolved.unit ?? 'seconds') as 'seconds' | 'minutes' | 'hours';
  const fmt = (v: number) => formatDuration(v, unit);

  if (from == null || from === 0) {
    if (to == null) return '< unknown';
    return `< ${fmt(to)}`;
  }
  if (to == null) return `> ${fmt(from)}`;
  return `${fmt(from)} - ${fmt(to)}`;
}
